import MonoIcon from '../../assets/icons/mono-Effect.svg';
import React, {useEffect, useState} from 'react';
import {getEffectAppliedImage, TransformValues} from '../../utils/helper';
import {CheckCircle, Colors} from '../../ui-components/Iconpack';
import {Slider} from '../../ui-components/Slider';
import Tracker from '../../lib/analytics/tracker';
import {EventNames, EventProperty} from '../../lib/analytics/events';
import {TransformationType} from '../../screens/TransformationV2/Transformation';
import {debounce} from 'lodash';

interface EffectsSubmenuProps {
    onTransformValuesChange: (values: {isGreyToneApplied: boolean; blur: number}) => void;
    transformationObject: TransformValues;
    addTransformation: (t: TransformationType) => void;
    removeTransformation: (t: TransformationType) => void;
    cdnUrl?: string;
}

export default function EffectsSubmenu({
    onTransformValuesChange,
    transformationObject,
    addTransformation,
    removeTransformation,
    cdnUrl,
}: EffectsSubmenuProps) {
    const [isGreyToneApplied, setGreyToneApplied] = useState(false);
    const [blurValue, setBlurValue] = useState(
        transformationObject.blur && transformationObject.blur > 0 ? transformationObject.blur : 0,
    );
    const [defaultBlurValue, setDefaultBlurValue] = useState(
        transformationObject.blur && transformationObject.blur > 0 ? transformationObject.blur : 0,
    );
    let greyscaleImage = '';
    let blurImage = '';
    if (cdnUrl) {
        greyscaleImage = getEffectAppliedImage(cdnUrl, 'greyscale');
        blurImage = getEffectAppliedImage(cdnUrl, 'blur');
    }

    const handleGreyToneEffect = () => {
        Tracker.trackEvent(EventNames.TRANSFORMATION_EFFECT_ADDED, {
            [EventProperty.Effect]: 'greyscale',
        });
        setGreyToneApplied(!isGreyToneApplied);
    };

    useEffect(() => {
        onTransformValuesChange({isGreyToneApplied: isGreyToneApplied, blur: blurValue});
        transformationObject.isGreyToneApplied
            ? removeTransformation(TransformationType.GRAY_EFFECT)
            : addTransformation(TransformationType.GRAY_EFFECT);
    }, [isGreyToneApplied]);

    const debouncedGetBlurValue = debounce((val: number) => {
        setBlurValue(val);
    }, 300);

    useEffect(() => {
        if (transformationObject.isGreyToneApplied) {
            setGreyToneApplied(transformationObject.isGreyToneApplied);
        }
    }, []);

    useEffect(() => {
        if (blurValue && blurValue > 0) {
            Tracker.trackEvent(EventNames.TRANSFORMATION_EFFECT_ADDED, {
                [EventProperty.Effect]: 'blur',
            });

            Tracker.trackEvent(EventNames.TRANSFORMATION_BLUR_MODIFIED, {
                [EventProperty.Blur]: blurValue,
            });

            onTransformValuesChange({isGreyToneApplied: isGreyToneApplied, blur: blurValue});
        } else {
            onTransformValuesChange({isGreyToneApplied: isGreyToneApplied, blur: 0});
        }
    }, [blurValue]);

    return (
        <div className='flex flex-col text-[14px] font-[700] text-[#FFFFFF]'>
            <div className='flex items-center'>
                <div className='block cursor-pointer items-center justify-center rounded-[6px] border-[2px] border-[#4E5BA6] bg-[#717BBC] p-[12px] md:mx-[16px] md:hidden'>
                    <Colors color='#FFFFFF' />
                </div>
                <div className='flex flex-col pl-[6px] text-[14px] font-[700] text-[#FFFFFF] md:pl-0'>
                    <span>EFFECTS</span>
                </div>
            </div>
            {!transformationObject.isSvgOrGif && (
                <div className='flex flex-col gap-2 border-b border-[#3E4784] py-8 text-[12px] font-[500] text-white'>
                    {blurImage && (
                        <div className='flex items-center gap-2'>
                            <img src={blurImage} alt='blur' className='h-10 w-10 rounded-[5px]' />
                            <span className=''>Blur</span>
                        </div>
                    )}
                    <div className='mb-4 flex items-center justify-between text-[12px] text-white'>
                        <span className='font-[600]'>Set blur intensity</span>
                        {blurValue ? (
                            <input
                                type='number'
                                value={blurValue}
                                className='h-7 w-[52px] rounded-[7px] border border-[#4E5BA6] bg-[#3E4784] px-[10px] py-[5px] text-center [&::-webkit-inner-spin-button]:appearance-none'
                                disabled={true}
                            />
                        ) : null}
                    </div>
                    <Slider
                        defaultValue={[defaultBlurValue]}
                        max={15}
                        step={0.5}
                        onValueChange={(val) => debouncedGetBlurValue(val[0])}
                    />
                </div>
            )}
            <div className='flex flex-col gap-2 pt-8 text-[12px] font-[500] text-white'>
                {greyscaleImage && (
                    <div className='flex items-center gap-2'>
                        <img src={greyscaleImage} alt='greyscale' className='h-10 w-10 rounded-[5px]' />
                        <span className=''>Grey Scale</span>
                    </div>
                )}
            </div>
            <div className={'cursor-pointer'} onClick={handleGreyToneEffect}>
                <img
                    className='absolute mt-[15px] cursor-pointer'
                    src={MonoIcon}
                    alt='mono-effect'
                    width={76}
                    height={77}
                />
                {isGreyToneApplied && (
                    <div className={'relative z-10 my-10 ml-6 flex'}>
                        <CheckCircle />
                    </div>
                )}
            </div>
        </div>
    );
}
